import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InviteForm } from '@/components/team/InviteForm';
import { MembersList } from '@/components/team/MembersList';
import { SeatUsageIndicator } from '@/components/team/SeatUsageIndicator';
import { getWorkspaceMembers, getPendingInvites, WorkspaceMember, PendingInvite } from '@/lib/api/workspaces';
import { supabase } from '@/integrations/supabase/client';
import { Users, UserPlus, Loader2 } from 'lucide-react';

interface TeamManagementSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  userId: string;
}

export const TeamManagementSheet = ({ open, onOpenChange, workspaceId, userId }: TeamManagementSheetProps) => {
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState<any>(null);
  
  const currentUserMember = members.find(m => m.user_id === userId);
  const currentUserRole = currentUserMember?.role;

  const loadData = async () => {
    try {
      setLoading(true);
      const [membersData, invitesData, { data: workspaceData }] = await Promise.all([
        getWorkspaceMembers(workspaceId),
        getPendingInvites(workspaceId),
        supabase.from('workspaces').select('*').eq('id', workspaceId).single(),
      ]);

      setMembers(membersData);
      setInvites(invitesData);
      setWorkspace(workspaceData);
    } catch (error) {
      console.error('Error loading team data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && workspaceId) {
      loadData();
    }
  }, [open, workspaceId]);

  const activeMembers = members.filter(m => m.seat_status === 'active');
  const usedSeats = activeMembers.length + invites.length;
  const maxSeats = workspace?.max_seats || 5;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl overflow-y-auto border-border bg-background">
        <SheetHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <SheetTitle className="text-lg font-semibold">
                Team-Verwaltung
              </SheetTitle>
              <SheetDescription className="text-sm">
                Teammitglieder und Einladungen verwalten
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Wird geladen...</p>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-5">
            <SeatUsageIndicator
              usedSeats={usedSeats}
              maxSeats={maxSeats}
              activeMembers={activeMembers.length}
              pendingInvites={invites.length}
            />

            <Tabs defaultValue="members" className="w-full">
              <TabsList className="grid w-full grid-cols-2 h-10">
                <TabsTrigger value="members" className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4" />
                  Team ({activeMembers.length})
                </TabsTrigger>
                <TabsTrigger value="invite" className="flex items-center gap-2 text-sm">
                  <UserPlus className="h-4 w-4" />
                  Einladen
                </TabsTrigger>
              </TabsList>

              <TabsContent value="members" className="mt-4">
                <MembersList 
                  members={members} 
                  invites={invites} 
                  currentUserRole={currentUserRole}
                  onRefresh={loadData}
                />
              </TabsContent>

              <TabsContent value="invite" className="mt-4">
                <InviteForm
                  workspaceId={workspaceId}
                  userId={userId}
                  onInviteCreated={loadData}
                />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
